/**
 * 应收逾期预测汇总
 */

export const RECEIVABLE_FORECAST_SUMMARY_MODEL =
  'aries.platform.AriesPlatformReceivableForecastSummary';

export const RECEIVABLE_FORECAST_SUMMARY_MENU_PATH =
  '/aries_core_platform/uiMenud8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3';

export const RECEIVABLE_FORECAST_SUMMARY_EXPORT_PATH =
  '/aries_core_platform/uiMenud8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3/ACTION#aries.platform.AriesPlatformReceivableForecastSummary#internalGotoListExportDialog';

export interface ReceivableForecastSummaryToolbarAuth {
  canExport: boolean;
}

export interface ReceivableForecastSummaryItem {
  id: string;
  lDate?: string;
  salesOfficeRegionName?: string;
  saleOfficeName?: string;
  remainingReceivableAmt?: string;
  overdueAmt?: string;
  monthEndDueAmt?: string;
  expectOverdueAmount?: string;
  expectRepayAmount?: string;
  actualReceiptAmt?: string;
  actualExpectDiff?: string;
  repaymentRate?: string;
  summaryRow?: boolean;
}

interface RawSummaryItem {
  id?: number | string;
  lDate?: string;
  salesOfficeRegionName?: string;
  saleOfficeName?: string;
  remainingReceivableAmt?: string;
  overdueAmt?: string;
  monthEndDueAmt?: string;
  expectOverdueAmount?: string;
  expectRepayAmount?: string;
  actualReceiptAmt?: string;
  actualExpectDiff?: string;
  repaymentRate?: string;
  summaryRow?: boolean | string | number;
}

interface SummaryQueryResponse {
  data?: {
    ariesPlatformReceivableForecastSummaryQuery?: {
      queryPage?: {
        content?: RawSummaryItem[];
        totalElements?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

interface LDateOptionsResponse {
  data?: {
    ariesPlatformReceivableForecastSummaryQuery?: {
      queryLDateOptions?: string[] | null;
    };
  };
  errors?: Array<{ message?: string }>;
}

function emptyToBlank(value?: string) {
  return (value ?? '').trim();
}

function toBool(value?: boolean | string | number) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function mapItem(raw: RawSummaryItem, fallbackLDate?: string): ReceivableForecastSummaryItem {
  return {
    id: String(raw.id ?? ''),
    lDate: raw.lDate || fallbackLDate || '',
    salesOfficeRegionName: emptyToBlank(raw.salesOfficeRegionName),
    saleOfficeName: emptyToBlank(raw.saleOfficeName),
    remainingReceivableAmt: emptyToBlank(raw.remainingReceivableAmt) || '-',
    overdueAmt: emptyToBlank(raw.overdueAmt) || '-',
    monthEndDueAmt: emptyToBlank(raw.monthEndDueAmt) || '-',
    expectOverdueAmount: emptyToBlank(raw.expectOverdueAmount) || '-',
    expectRepayAmount: emptyToBlank(raw.expectRepayAmount) || '-',
    actualReceiptAmt: emptyToBlank(raw.actualReceiptAmt) || '-',
    actualExpectDiff: emptyToBlank(raw.actualExpectDiff) || '-',
    repaymentRate: emptyToBlank(raw.repaymentRate) || '-',
    summaryRow: toBool(raw.summaryRow),
  };
}

async function canAccessViewAction(actionName: string): Promise<boolean> {
  try {
    const response = await fetch(`/pamirs/base`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        module: 'base',
      },
      body: JSON.stringify({
        operationName: null,
        variables: {
          needCompileView: false,
          needCompileMask: false,
          model: RECEIVABLE_FORECAST_SUMMARY_MODEL,
          name: actionName,
          path: RECEIVABLE_FORECAST_SUMMARY_MENU_PATH,
        },
        query: `query ($model: String, $name: String, $needCompileView: Boolean = false, $needCompileMask: Boolean = false) {
  viewActionQuery {
    load(
      request: {model: $model, name: $name, needCompileView: $needCompileView, needCompileMask: $needCompileMask}
    ) {
      name
    }
  }
}`,
      }),
    });
    if (!response.ok) return false;
    const result = await response.json();
    if (result?.errors?.length) return false;
    return !!result?.data?.viewActionQuery?.load?.name;
  } catch {
    return false;
  }
}

export async function queryReceivableForecastSummaryToolbarAuth(): Promise<ReceivableForecastSummaryToolbarAuth> {
  try {
    return { canExport: await canAccessViewAction('internalGotoListExportDialog') };
  } catch (error) {
    console.warn('查询应收逾期预测汇总工具栏权限失败', error);
    return { canExport: false };
  }
}

export async function queryForecastSummaryLDateOptions(): Promise<string[]> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: `{
  ariesPlatformReceivableForecastSummaryQuery {
    queryLDateOptions
  }
}`,
    }),
  });
  if (!response.ok) {
    throw new Error(`查询日期选项失败: ${response.status}`);
  }
  const result: LDateOptionsResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询日期选项失败');
  }
  const list = result.data?.ariesPlatformReceivableForecastSummaryQuery?.queryLDateOptions;
  return Array.isArray(list) ? list.filter((d) => !!d && String(d).trim()) : [];
}

/** 在 21 号日期列表中选离今天最近的一个（优先不大于今天） */
export function pickClosestLDate(dates: string[]): string | undefined {
  if (!dates?.length) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let bestPast: string | undefined;
  let bestFuture: string | undefined;
  let bestPastDiff = Number.POSITIVE_INFINITY;
  let bestFutureDiff = Number.POSITIVE_INFINITY;

  for (const raw of dates) {
    const text = String(raw || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) continue;
    const d = new Date(`${text}T00:00:00`);
    if (Number.isNaN(d.getTime())) continue;
    const diff = Math.abs(d.getTime() - today.getTime());
    if (d.getTime() <= today.getTime()) {
      if (diff < bestPastDiff) {
        bestPastDiff = diff;
        bestPast = text;
      }
    } else if (diff < bestFutureDiff) {
      bestFutureDiff = diff;
      bestFuture = text;
    }
  }
  return bestPast || bestFuture || dates[0];
}

export async function queryReceivableForecastSummaryList(
  lDate: string,
): Promise<ReceivableForecastSummaryItem[]> {
  const date = emptyToBlank(lDate);
  if (!date) {
    throw new Error('日期不能为空');
  }
  const queryData = JSON.stringify({ lDate: date });
  const query = `{
  ariesPlatformReceivableForecastSummaryQuery {
    queryPage(
      page: {currentPage: 1, size: 500}
      queryWrapper: {queryData: ${JSON.stringify(queryData)}}
    ) {
      content {
        id
        lDate
        salesOfficeRegionName
        saleOfficeName
        remainingReceivableAmt
        overdueAmt
        monthEndDueAmt
        expectOverdueAmount
        expectRepayAmount
        actualReceiptAmt
        actualExpectDiff
        repaymentRate
        summaryRow
      }
      totalElements
    }
  }
}`;

  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query,
    }),
  });

  if (!response.ok) {
    throw new Error(`汇总查询失败: ${response.status}`);
  }

  const result: SummaryQueryResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '汇总查询失败');
  }

  const content = result.data?.ariesPlatformReceivableForecastSummaryQuery?.queryPage?.content ?? [];
  return content.map((item) => mapItem(item, date));
}

export function buildForecastSummaryExportQueryData(lDate?: string): Record<string, unknown> {
  return { lDate: emptyToBlank(lDate) };
}
